import {
  BUTTON_COLORS,
  BUTTON_TYPE,
  CUSTOM_SIZES,
  DYNAMIC_POPUP_TYPES,
  ERROR_TYPE,
  GEOLOCATION_STEPS,
  GR_TRACKING_EVENT,
  POPUP_TEST_IDS,
  POPUP_TYPES,
  SECONDARY_POPUP_TYPES,
} from './constants';
import { FooterOptions, GameErrors, IPopup } from './types';

import i18n from '@/utilities/translation/i18nInit';
import { PRODUCTS } from '@/utilities/constants/game';
import { useStore } from '@/store/useStore';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

const t = (key: string, options?: Record<string, string>) =>
  i18n.t(key, options);

/** Fires a Datadog RUM action for a GEOLOCATION_REFRESH popup interaction. */
const trackGrEvent = (
  event: (typeof GR_TRACKING_EVENT)[keyof typeof GR_TRACKING_EVENT],
) =>
  Logger.logRumAction({
    name: `GR_TRACKING_ACTIONS_${event}`,
    useCase: DdEventCategory.Geolocation,
    eventType: DdEventType.Info,
    service: 'geolocation-web',
  });

const getRuntimeProduct = () => {
  try {
    return (
      useStore.getState?.().runtime?.product ?? process.env.PRODUCT ?? 'casino'
    );
  } catch {
    return process.env.PRODUCT ?? 'casino';
  }
};

const PRODUCT =
  getRuntimeProduct()?.toLowerCase() === PRODUCTS.POKER ? 'poker' : 'casino';

const BUTTON_MESSAGES = {
  TRY_AGAIN: t('popup.buttons.tryAgain'),
  GO_HOME: t('popup.buttons.goHome'),
  DEPOSIT: t('popup.buttons.deposit'),
  BACK_TO_GAME: t('popup.buttons.backToGame'),
  DISMISS: t('popup.buttons.dismiss'),
  LEAVE_TO_SUPPORT: t('popup.buttons.leaveToSupport'),
  BACK_TO_LAST_GAME: t('popup.buttons.backToLastGame'),
  LEAVE_TO_GO_HOME: t('popup.buttons.leaveToGoHome'),
  CONTINUE_PLAYING: t('popup.buttons.continuePlaying'),
  FREE_SPIN_CHOOSE_FREE_SPIN: t('popup.buttons.freeSpinChooseFreeSpin'),
  FREE_SPIN_CHOOSE_REAL_MONEY: t('popup.buttons.freeSpinChooseRealMoney'),
  FREE_SPIN_FINISH_SPINS: t('popup.buttons.freeSpinFinishSpins'),
  CONTINUE_WITHOUT_JACKPOTS: t('popup.buttons.continueWithoutJackpots'),
  EXIT_GAME: t('gameWindow.exitPopup.buttonExit'),
  CANCEL: t('gameWindow.exitPopup.buttonCancel'),
  EXIT_GAME_RECENTLY_PLAYED: t('gameWindow.exitPopupRecentlyPlayed.buttonExit'),
  GENERAL_ERROR_DISMISS: t('popup.buttons.generalErrorDismiss'),
} as const;

const ERROR_MESSAGES = {
  [ERROR_TYPE.GENERIC]: {
    main: {
      popupMessage: t('popup.errors.generic.main.popupMessage'),
      popupName: t('popup.errors.generic.main.popupName', {
        product: PRODUCT,
      }),
    },
    secondary: {
      popupName: t('popup.errors.generic.secondary.popupName'),
      firstMessage: t('popup.errors.generic.secondary.firstMessage'),
      actionMessage: t('popup.errors.generic.secondary.actionMessage'),
    },
  },
  [ERROR_TYPE.INSUFFICIENT_FUNDS]: {
    main: {
      popupMessage: t('popup.errors.insufficientFunds.main.popupMessage'),
      popupName: t('popup.errors.insufficientFunds.main.popupName'),
    },
    secondary: {
      popupName: t('popup.errors.insufficientFunds.secondary.popupName'),
      firstMessage: t('popup.errors.insufficientFunds.secondary.firstMessage'),
      actionMessage: t(
        'popup.errors.insufficientFunds.secondary.actionMessage',
      ),
    },
  },
  [ERROR_TYPE.SESSION_EXPIRED]: {
    main: {
      popupMessage: t('gameWindow.sessionExpiredPopup.message2'),
      popupName: t('gameWindow.sessionExpiredPopup.message1'),
    },
  },
  [ERROR_TYPE.GAME_IN_PROGRESS]: {
    main: {
      popupMessage: t('popup.errors.gameInProgress.main.popupMessage'),
      popupName: t('popup.errors.gameInProgress.main.popupName'),
    },
  },
  [ERROR_TYPE.GAME_STOPPED_WORKING]: {
    main: {
      popupMessage: t('popup.errors.gameStoppedWorking.main.popupMessage'),
      popupName: t('popup.errors.gameStoppedWorking.main.popupName'),
    },
  },
  [ERROR_TYPE.CONNECTION_LOST]: {
    main: {
      popupMessage: t('popup.errors.connectionLost.main.popupMessage'),
      popupName: t('popup.errors.connectionLost.main.popupName'),
    },
  },
  [ERROR_TYPE.TOO_MANY_GAMES_OPEN]: {
    main: {
      popupMessage: t('popup.errors.tooManyGamesOpen.main.popupMessage'),
      popupName: t('popup.errors.tooManyGamesOpen.main.popupName'),
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_START]: {
    main: {
      popupMessage: t('popup.errors.freeSpinDialogStart.main.popupMessage'),
      popupName: t('popup.errors.freeSpinDialogStart.main.popupName'),
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE]: {
    main: {
      popupMessage: t('popup.errors.freeSpinDialogChoose.main.popupMessage'),
      popupName: t('popup.errors.freeSpinDialogChoose.main.popupName'),
    },
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_FINISH]: {
    main: {
      popupMessage: t('popup.errors.freeSpinDialogFinish.main.popupMessage'),
      popupName: t('popup.errors.freeSpinDialogFinish.main.popupName'),
    },
  },
  [ERROR_TYPE.UNKNOWN]: {
    main: {
      popupMessage: t('popup.errors.unknown.main.popupMessage'),
      popupName: t('popup.errors.unknown.main.popupName', {
        product: PRODUCT,
      }),
    },
  },
  [ERROR_TYPE.GENERIC_DISMISSABLE]: {
    main: {
      popupMessage: t('popup.errors.genericDismissable.main.popupMessage'),
      popupName: t('popup.errors.genericDismissable.main.popupName'),
    },
  },
  [ERROR_TYPE.NET_POSITION_SIMPLE]: {
    main: {
      popupMessage: t('popup.errors.netPositionSimple.main.popupMessage'),
      popupName: t('popup.errors.netPositionSimple.main.popupName'),
    },
  },
} as const;

const GenericFooter: FooterOptions = {
  secondaryPopup: SECONDARY_POPUP_TYPES.GENERIC,
  messages: {
    firstMessage: ERROR_MESSAGES[ERROR_TYPE.GENERIC].secondary.firstMessage,
    actionMessage: ERROR_MESSAGES[ERROR_TYPE.GENERIC].secondary.actionMessage,
  },
  testIds: {
    text: POPUP_TEST_IDS.ERROR_FOOTER_TEXT,
    action: POPUP_TEST_IDS.CONTACT_SUPPORT_LINK,
  },
};

const InsufficientFundsFooter: FooterOptions = {
  secondaryPopup: SECONDARY_POPUP_TYPES.INSUFFICIENT_FUNDS,
  rgIcon: true,
  includeErrorCode: false,
  messages: {
    firstMessage:
      ERROR_MESSAGES[ERROR_TYPE.INSUFFICIENT_FUNDS].secondary.firstMessage,
    actionMessage:
      ERROR_MESSAGES[ERROR_TYPE.INSUFFICIENT_FUNDS].secondary.actionMessage,
  },
  testIds: {
    text: POPUP_TEST_IDS.INSUFFICIENT_BALANCE_FOOTER_TEXT,
    action: POPUP_TEST_IDS.INSUFFICIENT_BALANCE_FOOTER_LINK,
  },
};

/**
 * GLS errors - not actual provider errors. To be moved into the game
 * window (gw) directly in the future; this map is expected to be deleted
 * then.
 */
export const GAME_ERRORS: GameErrors = {
  [ERROR_TYPE.FAILED_TO_FETCH_GAME]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupName,
    },
    errorType: ERROR_TYPE.GENERIC,
  },
  [ERROR_TYPE.GENERIC]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.TRY_AGAIN,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
        {
          buttonMessage: BUTTON_MESSAGES.GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.GENERIC,
  },
  [ERROR_TYPE.INSUFFICIENT_FUNDS]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.DEPOSIT,
          buttonType: BUTTON_TYPE.DEPOSIT,
          dataTestId: POPUP_TEST_IDS.ADD_FUNDS_TO_BALANCE_BUTTON,
        },
        {
          buttonMessage: BUTTON_MESSAGES.BACK_TO_GAME,
          buttonType: BUTTON_TYPE.DISMISS,
          dataTestId: POPUP_TEST_IDS.DISMISS_ERROR_MODAL_BUTTON,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.INSUFFICIENT_FUNDS].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.INSUFFICIENT_FUNDS].main.popupName,
    },
    secondary: InsufficientFundsFooter,
    errorType: ERROR_TYPE.INSUFFICIENT_FUNDS,
  },
  [ERROR_TYPE.SESSION_EXPIRED]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.DISMISS,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
        {
          buttonMessage: BUTTON_MESSAGES.GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.SESSION_EXPIRED].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.SESSION_EXPIRED].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.SESSION_EXPIRED,
  },
  [ERROR_TYPE.GAME_IN_PROGRESS]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.BACK_TO_LAST_GAME,
          buttonType: BUTTON_TYPE.BACK_TO_LAST_GAME,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.GAME_IN_PROGRESS].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GAME_IN_PROGRESS].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.GAME_IN_PROGRESS,
  },
  [ERROR_TYPE.GAME_STOPPED_WORKING]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
        {
          buttonMessage: BUTTON_MESSAGES.TRY_AGAIN,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.GAME_STOPPED_WORKING].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GAME_STOPPED_WORKING].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.GAME_STOPPED_WORKING,
  },
  [ERROR_TYPE.CONNECTION_LOST]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.TRY_AGAIN,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.CONNECTION_LOST].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.CONNECTION_LOST].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.CONNECTION_LOST,
  },
  [ERROR_TYPE.TOO_MANY_GAMES_OPEN]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.TOO_MANY_GAMES_OPEN].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.TOO_MANY_GAMES_OPEN].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.TOO_MANY_GAMES_OPEN,
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_START]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.FREE_SPIN_CHOOSE_FREE_SPIN,
          buttonType: BUTTON_TYPE.FREE_SPIN_CHOOSE_FREE_SPIN,
        },
        {
          buttonMessage: BUTTON_MESSAGES.FREE_SPIN_CHOOSE_REAL_MONEY,
          buttonType: BUTTON_TYPE.FREE_SPIN_CHOOSE_REAL_MONEY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_START].main.popupMessage,
      popupName:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_START].main.popupName,
    },
    errorType: ERROR_TYPE.FREE_SPIN_DIALOG_START,
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE]: {
    props: {
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE].main.popupMessage,
      popupName:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE].main.popupName,
    },
    errorType: ERROR_TYPE.FREE_SPIN_DIALOG_CHOOSE,
  },
  [ERROR_TYPE.FREE_SPIN_DIALOG_FINISH]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.FREE_SPIN_FINISH_SPINS,
          buttonType: BUTTON_TYPE.FREE_SPIN_FINISH_SPINS,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_FINISH].main.popupMessage,
      popupName:
        ERROR_MESSAGES[ERROR_TYPE.FREE_SPIN_DIALOG_FINISH].main.popupName,
    },
    errorType: ERROR_TYPE.FREE_SPIN_DIALOG_START,
  },
  [ERROR_TYPE.GENERAL_ERROR]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.GENERAL_ERROR_DISMISS,
          buttonType: BUTTON_TYPE.GENERAL_ERROR_DISMISS,
        },
      ],
      popupName: t('generalError.popupName'),
    },
    errorType: ERROR_TYPE.GENERAL_ERROR,
  },
  [ERROR_TYPE.NET_POSITION_SIMPLE]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.NET_POSITION_SIMPLE].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.NET_POSITION_SIMPLE].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.NET_POSITION_SIMPLE,
  },
  [ERROR_TYPE.FAILED_TO_REDIRECT]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.TRY_AGAIN,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
        {
          buttonMessage: BUTTON_MESSAGES.GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      // Subject to change after product design confirmation
      popupMessage: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GENERIC].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.FAILED_TO_REDIRECT,
  },
  [ERROR_TYPE.UNKNOWN]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.CONTINUE_PLAYING,
          buttonType: BUTTON_TYPE.DISMISS,
          dataTestId: POPUP_TEST_IDS.DISMISS_ERROR_MODAL_BUTTON,
        },
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage: ERROR_MESSAGES[ERROR_TYPE.UNKNOWN].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.UNKNOWN].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.UNKNOWN,
  },
  [ERROR_TYPE.GENERIC_DISMISSABLE]: {
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.CONTINUE_PLAYING,
          buttonType: BUTTON_TYPE.DISMISS,
          dataTestId: POPUP_TEST_IDS.DISMISS_ERROR_MODAL_BUTTON,
        },
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        },
      ],
      popupMessage:
        ERROR_MESSAGES[ERROR_TYPE.GENERIC_DISMISSABLE].main.popupMessage,
      popupName: ERROR_MESSAGES[ERROR_TYPE.GENERIC_DISMISSABLE].main.popupName,
    },
    secondary: GenericFooter,
    errorType: ERROR_TYPE.GENERIC_DISMISSABLE,
  },
  [SECONDARY_POPUP_TYPES.GENERIC]: {
    errorType: SECONDARY_POPUP_TYPES.GENERIC,
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_SUPPORT,
          buttonType: BUTTON_TYPE.SUPPORT,
          dataTestId: POPUP_TEST_IDS.YES_LEAVE_CONTACT_SUPPORT_BUTTON,
        },
        {
          buttonMessage: BUTTON_MESSAGES.BACK_TO_GAME,
          buttonType: BUTTON_TYPE.TRY_AGAIN,
        },
      ],

      popupName: ERROR_MESSAGES[ERROR_TYPE.GENERIC].secondary.popupName,
    },
  },
  [SECONDARY_POPUP_TYPES.INSUFFICIENT_FUNDS]: {
    errorType: SECONDARY_POPUP_TYPES.INSUFFICIENT_FUNDS,
    props: {
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_SUPPORT,
          buttonType: BUTTON_TYPE.DEPOSIT_LIMITS,
        },
        {
          buttonMessage: BUTTON_MESSAGES.BACK_TO_GAME,
          buttonType: BUTTON_TYPE.DISMISS,
        },
      ],
      popupName:
        ERROR_MESSAGES[ERROR_TYPE.INSUFFICIENT_FUNDS].secondary.popupName,
    },
  },
};

export const EXIT_POPUP_STRUCTURE: IPopup = {
  props: {
    customSize: CUSTOM_SIZES.SMALL,
    closeButton: true,
    horizontalButtons: true,
    buttons: [
      {
        buttonMessage: BUTTON_MESSAGES.EXIT_GAME,
        buttonType: BUTTON_TYPE.EXIT_GAME,
        buttonClass: BUTTON_COLORS.BLUE,
        dataTestId: POPUP_TEST_IDS.EXIT_GAME_CONFIRM_BUTTON,
      },
      {
        buttonMessage: BUTTON_MESSAGES.CANCEL,
        buttonType: BUTTON_TYPE.DISMISS,
        buttonClass: BUTTON_COLORS.MAIN,
        dataTestId: POPUP_TEST_IDS.EXIT_GAME_CANCEL_BUTTON,
      },
    ],
    popupMessage: t('gameWindow.exitPopup.message'),
  },
} as const;

export const EXIT_RECENTLY_PLAYED_POPUP_STRUCTURE: IPopup = {
  props: {
    customSize: CUSTOM_SIZES.SMALL,
    closeButton: true,
    horizontalButtons: true,
    buttons: [
      {
        buttonMessage: BUTTON_MESSAGES.EXIT_GAME_RECENTLY_PLAYED,
        buttonType: BUTTON_TYPE.EXIT_GAME_RECENTLY_PLAYED,
        buttonClass: BUTTON_COLORS.BLUE,
        dataTestId: POPUP_TEST_IDS.LAUNCH_GAME_BUTTON,
      },
      {
        buttonMessage: BUTTON_MESSAGES.CANCEL,
        buttonType: BUTTON_TYPE.DISMISS,
        buttonClass: BUTTON_COLORS.MAIN,
        dataTestId: POPUP_TEST_IDS.RECENTLY_PLAYED_CANCEL_BUTTON,
      },
    ],
    popupMessage: t('gameWindow.exitPopupRecentlyPlayed.message'),
  },
} as const;

export const SWJ_POPUP_STRUCTURE: IPopup = {
  props: {
    buttons: [
      {
        buttonMessage: BUTTON_MESSAGES.CONTINUE_WITHOUT_JACKPOTS,
        buttonType: BUTTON_TYPE.CONTINUE_WITHOUT_JACKPOTS,
        dataTestId: POPUP_TEST_IDS.CONTINUE_WITHOUT_JACKPOTS_BUTTON,
      },
      {
        buttonMessage: BUTTON_MESSAGES.EXIT_GAME,
        buttonType: BUTTON_TYPE.EXIT_GAME,
        dataTestId: POPUP_TEST_IDS.SWJ_EXIT_GAME_BUTTON,
      },
    ],
    popupMessage: t('popup.swj.popupMessage'),
    popupName: t('popup.swj.popupName', { product: PRODUCT }),
  },
  secondary: GenericFooter,
  errorType: POPUP_TYPES.SWJ,
} as const;

/**
 * Three-step popup shown when geolocation is invalidated mid-game:
 *  - LOADING: re-checking the user's location (game paused).
 *  - SUCCESS: location confirmed, auto-closes after 1s and returns to the game.
 *  - ERROR:   location could not be re-verified, user goes home.
 *
 * `dynamicProps` overrides the base `props` for the current step. The main CTA
 * (leave to go home) is shared across steps; each step adds its own DD tracking.
 */
export const GEOLOCATION_REFRESH_POPUP_STRUCTURE: IPopup = {
  props: {
    dynamicSteps: true,
    popupName: t('popup.geolocationRefresh.loading.popupName'),
    popupMessage: t('popup.geolocationRefresh.loading.popupMessage'),
    mainButton: BUTTON_TYPE.GO_TO_LOBBY,
    mainButtonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
    mainButtonColor: BUTTON_COLORS.SECONDARY,
    buttons: [
      {
        buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
        buttonType: BUTTON_TYPE.GO_TO_LOBBY,
        buttonClass: BUTTON_COLORS.SECONDARY,
      },
    ],
  },
  dynamicProps: {
    [GEOLOCATION_STEPS.LOADING]: {
      mainButtonExtraAction: () =>
        trackGrEvent(GR_TRACKING_EVENT.INVALIDATED_MODAL_LEAVE),
    },
    [GEOLOCATION_STEPS.SUCCESS]: {
      autoClose: {
        ms: 1000,
        targetedPopup: DYNAMIC_POPUP_TYPES.GEOLOCATION_REFRESH,
      },
      mainButtonExtraAction: () =>
        trackGrEvent(GR_TRACKING_EVENT.SUCCESS_MODAL_LEAVE),
      popupName: t('popup.geolocationRefresh.success.popupName'),
      popupMessage: t('popup.geolocationRefresh.success.popupMessage'),
    },
    [GEOLOCATION_STEPS.ERROR]: {
      mainButtonExtraAction: () =>
        trackGrEvent(GR_TRACKING_EVENT.FAIL_MODAL_LEAVE),
      popupName: t('popup.geolocationRefresh.error.popupName'),
      popupMessage: t('popup.geolocationRefresh.error.popupMessage'),
      mainButtonColor: BUTTON_COLORS.BLUE,
      buttons: [
        {
          buttonMessage: BUTTON_MESSAGES.LEAVE_TO_GO_HOME,
          buttonType: BUTTON_TYPE.GO_TO_LOBBY,
          buttonClass: BUTTON_COLORS.BLUE,
        },
      ],
    },
  },
};
